import React, { useState } from 'react';
import { Sparkles, Plus, ChevronDown, Trophy, TrendingUp, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer } from 'recharts';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { FEATURE_GATES } from '../../config/limits';

/**
 * Preview of the Strategy tab shown below the Plus tier. Three static, READABLE worked
 * examples — Compare / Split Buy / Split Sell — matching the real components'
 * layouts, browsable via the sub-tab bar. The bottom of each example fades into a
 * single upgrade CTA.
 *
 * ONLY the sub-tab selection is interactive. Everything else is display-only:
 * nothing is editable, nothing saves, and it reads NOTHING from the user's
 * portfolio, StrategyContext, or saved plans. Every figure is hard-coded.
 */

const SUB_TABS = ['Compare', 'Split Buy', 'Split Sell'] as const;
type Tab = typeof SUB_TABS[number];

/** Read-only pseudo-input — looks like a compact tier input, but is a div. */
function Field({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">
      {children}
    </div>
  );
}

function XrpIcon() {
  return (
    <span
      className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
      style={{ background: 'rgba(0,165,223,0.18)', color: '#00a5df' }}
    >
      ✕
    </span>
  );
}
function BtcIcon() {
  return (
    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-orange-400 flex-shrink-0 bg-orange-500/20">
      ₿
    </span>
  );
}

// ── Split Buy example — XRP, budget A$1,000, live A$1.40 ───────────────────────

const BUY_GRID = '14px 120px 72px minmax(84px,1fr) minmax(96px,1fr) minmax(112px,1fr)';
const BUY_TIERS = [
  { dot: 'bg-blue-500',    coinColor: 'text-blue-400',    target: '1.45', alloc: '30', vsLive: '+3.6%',  vsColor: 'text-amber-400',   budget: 'A$300.00', coins: '206.8966' },
  { dot: 'bg-violet-500',  coinColor: 'text-violet-400',  target: '1.35', alloc: '40', vsLive: '-3.6%',  vsColor: 'text-emerald-400', budget: 'A$400.00', coins: '296.2963' },
  { dot: 'bg-emerald-500', coinColor: 'text-emerald-400', target: '1.25', alloc: '30', vsLive: '-10.7%', vsColor: 'text-emerald-400', budget: 'A$300.00', coins: '240.0000' },
];

function BuyBody() {
  return (
    <>
      {/* Header: asset + budget */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5 min-h-[16px]">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Asset</span>
            <span className="text-[11px] text-slate-500">live <span className="text-slate-300 font-mono">A$1.40</span></span>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 flex items-center gap-2">
            <XrpIcon /> XRP
          </div>
        </div>
        <div>
          <span className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Total Budget (AUD)</span>
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100">1,000</div>
        </div>
      </div>

      {/* Buy tiers table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Buy Tiers</span>
          <span className="text-xs text-emerald-400">✓ 100% allocated</span>
        </div>
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <div className="grid items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-800/40" style={{ gridTemplateColumns: BUY_GRID }}>
            <span />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Target</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Alloc %</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">vs Live</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Budget</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Coins</span>
          </div>
          {BUY_TIERS.map((t, i) => (
            <div key={i} className="grid items-center gap-2 px-3 border-b border-slate-700/50" style={{ gridTemplateColumns: BUY_GRID, minHeight: 34 }}>
              <span className={`w-2 h-2 rounded-full ${t.dot}`} />
              <Field>{t.target}</Field>
              <Field>{t.alloc}</Field>
              <span className={`text-xs tabular-nums text-right ${t.vsColor}`}>{t.vsLive}</span>
              <span className="text-xs tabular-nums text-right text-slate-200">{t.budget}</span>
              <span className={`text-xs tabular-nums text-right ${t.coinColor}`}>{t.coins} <span className="text-[11px] text-slate-500">XRP</span></span>
            </div>
          ))}
        </div>
        <div className="mt-2 h-[5px] rounded-full overflow-hidden flex bg-slate-800">
          <div className="bg-blue-500" style={{ width: '30%' }} />
          <div className="bg-violet-500" style={{ width: '40%' }} />
          <div className="bg-emerald-500" style={{ width: '30%' }} />
        </div>
      </div>
    </>
  );
}

function BuyFooter() {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="grid grid-cols-2 gap-4 lg:flex-none">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total coins</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">743.1929 <span className="text-[11px] font-normal text-slate-500">XRP</span></p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Avg buy price</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">A$1.3455</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4 lg:flex-1 lg:border-l lg:border-slate-700 lg:pl-4">
          <div className="min-w-[160px]">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Target price (AUD)</p>
            <div className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">3.00</div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Value</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">A$2,229.58</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Return</p>
            <p className="text-sm font-semibold tabular-nums text-emerald-400">+123.0%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Split Sell example — 1,000 XRP held, avg buy A$0.80, live A$1.40 ───────────

const SELL_GRID = '14px 112px 64px minmax(72px,0.9fr) minmax(84px,1fr) minmax(100px,1.1fr) minmax(84px,1fr)';
const SELL_TIERS = [
  { dot: 'bg-blue-500',    coinColor: 'text-blue-400',    sell: '1.80', alloc: '40', vsLive: '+28.6%',  coins: '400', proceeds: 'A$720.00', pnl: '+A$400.00' },
  { dot: 'bg-violet-500',  coinColor: 'text-violet-400',  sell: '2.20', alloc: '35', vsLive: '+57.1%',  coins: '350', proceeds: 'A$770.00', pnl: '+A$490.00' },
  { dot: 'bg-emerald-500', coinColor: 'text-emerald-400', sell: '3.00', alloc: '25', vsLive: '+114.3%', coins: '250', proceeds: 'A$750.00', pnl: '+A$550.00' },
];

function SellBody() {
  return (
    <>
      {/* Header: asset + coins held + avg buy price */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5 min-h-[16px]">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Asset</span>
            <span className="text-[11px] text-slate-500">live <span className="text-slate-300 font-mono">A$1.40</span></span>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 flex items-center gap-2">
            <XrpIcon /> XRP
          </div>
        </div>
        <div>
          <span className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Coins Held</span>
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100">1,000</div>
        </div>
        <div>
          <span className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Avg Buy Price (AUD)</span>
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100">0.80</div>
        </div>
      </div>

      {/* Sell tiers table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sell Tiers</span>
          <span className="text-xs text-emerald-400">✓ 100% allocated</span>
        </div>
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <div className="grid items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-800/40" style={{ gridTemplateColumns: SELL_GRID }}>
            <span />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Sell</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Alloc %</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">vs Live</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Coins</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Proceeds</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">P&amp;L</span>
          </div>
          {SELL_TIERS.map((t, i) => (
            <div key={i} className="grid items-center gap-2 px-3 border-b border-slate-700/50" style={{ gridTemplateColumns: SELL_GRID, minHeight: 34 }}>
              <span className={`w-2 h-2 rounded-full ${t.dot}`} />
              <Field>{t.sell}</Field>
              <Field>{t.alloc}</Field>
              {/* vs Live inverted for selling: above live is the good case → green */}
              <span className="text-xs tabular-nums text-right text-emerald-400">{t.vsLive}</span>
              <span className={`text-xs tabular-nums text-right ${t.coinColor}`}>{t.coins} <span className="text-[11px] text-slate-500">XRP</span></span>
              <span className="text-xs tabular-nums text-right text-slate-200">{t.proceeds}</span>
              <span className="text-xs tabular-nums text-right text-emerald-400">{t.pnl}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 h-[5px] rounded-full overflow-hidden flex bg-slate-800">
          <div className="bg-blue-500" style={{ width: '40%' }} />
          <div className="bg-violet-500" style={{ width: '35%' }} />
          <div className="bg-emerald-500" style={{ width: '25%' }} />
        </div>
      </div>
    </>
  );
}

function SellFooter() {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="grid grid-cols-3 gap-4 lg:flex-1">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Coins sold</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">1,000 <span className="text-[11px] font-normal text-slate-500">XRP</span></p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Avg exit price</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">A$2.24</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total proceeds</p>
            <p className="text-sm font-semibold text-slate-100 tabular-nums">A$2,240.00</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4 lg:border-l lg:border-slate-700 lg:pl-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total P&amp;L</p>
            <p className="text-sm font-semibold tabular-nums text-emerald-400">+A$1,440.00</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Return</p>
            <p className="text-sm font-semibold tabular-nums text-emerald-400">+180.0%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Compare example — XRP vs Bitcoin, normalised % return ──────────────────────

const CMP_A = '#10b981'; // teal (Asset A)
const CMP_B = '#38bdf8'; // sky  (Asset B)
const COMPARE_DATA = [
  { label: 'Jul 1',  a: 0,  b: 0 },
  { label: 'Jul 4',  a: 3,  b: 1 },
  { label: 'Jul 8',  a: 5,  b: 2 },
  { label: 'Jul 12', a: 2,  b: 3 },
  { label: 'Jul 16', a: 8,  b: 2 },
  { label: 'Jul 20', a: 11, b: 4 },
  { label: 'Jul 24', a: 9,  b: 5 },
  { label: 'Jul 28', a: 14, b: 4 },
  { label: 'Jul 31', a: 18, b: 7 },
];

function ComparePicker({ color, label, icon, name, symbol }: {
  color: string; label: string; icon: React.ReactNode; name: string; symbol: string;
}) {
  return (
    <div className="flex-1 min-w-[180px] space-y-1.5">
      <div className="flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Asset {label}</span>
      </div>
      <div className="w-full flex items-center gap-2.5 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm">
        {icon}
        <span className="font-semibold text-slate-100 truncate flex-1">{name}</span>
        <span className="text-xs text-slate-500 flex-shrink-0">{symbol}</span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </div>
    </div>
  );
}

function CompareBody() {
  return (
    <>
      <div className="mb-1">
        <p className="text-slate-100" style={{ fontSize: 15, fontWeight: 500 }}>Compare Assets</p>
        <p className="text-slate-400 mt-0.5" style={{ fontSize: 12 }}>Normalised % return from period start - compare up to 5 assets</p>
      </div>

      {/* Asset pickers */}
      <div className="flex flex-wrap gap-3">
        <ComparePicker color={CMP_A} label="A" icon={<XrpIcon />} name="XRP" symbol="XRP" />
        <ComparePicker color={CMP_B} label="B" icon={<BtcIcon />} name="Bitcoin" symbol="BTC" />
      </div>
      <div className="inline-flex items-center gap-1.5 text-xs text-slate-400 border border-dashed border-slate-700 px-3 py-2 rounded-xl">
        <Plus className="w-3.5 h-3.5" /> Add Asset
      </div>

      {/* Range + Compare */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-800 rounded-xl p-1">
          {['7D', '1M', '3M', '1Y'].map((r) => {
            const active = r === '1M';
            return (
              <span
                key={r}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${active ? 'bg-slate-700 text-slate-100 shadow-sm' : 'text-slate-500'}`}
              >
                {r}
              </span>
            );
          })}
        </div>
        <span className="flex items-center gap-2 px-4 py-2 bg-white text-[#0f0f0f] text-sm font-semibold rounded-xl">
          <RefreshCw className="w-3.5 h-3.5" /> Compare
        </span>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 rounded-full" style={{ backgroundColor: CMP_A }} />
          <span className="text-sm text-slate-300 font-medium">XRP</span>
          <span className="text-xs text-slate-500">(XRP)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 rounded-full" style={{ backgroundColor: CMP_B }} />
          <span className="text-sm text-slate-300 font-medium">Bitcoin</span>
          <span className="text-xs text-slate-500">(BTC)</span>
        </div>
      </div>

      {/* Chart — same normalised %-return shape as the real Compare */}
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={COMPARE_DATA} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={50} />
          <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`} width={44} />
          <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 4" />
          <Line type="monotone" dataKey="a" stroke={CMP_A} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="b" stroke={CMP_B} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

function CompareCard({ color, icon, name, symbol, start, end, ret, winner }: {
  color: string; icon: React.ReactNode; name: string; symbol: string; start: string; end: string; ret: string; winner?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[180px] bg-slate-800/50 rounded-xl border border-slate-700/60 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
          {icon}
          <div className="min-w-0">
            <p className="font-semibold text-slate-100 text-sm truncate">{name}</p>
            <p className="text-xs text-slate-500">{symbol}</p>
          </div>
        </div>
        {winner && (
          <div className="flex items-center gap-1 text-xs font-semibold text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded-full border border-yellow-500/20 flex-shrink-0">
            <Trophy className="w-3 h-3" /> Winner
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">Start</span>
          <span className="text-slate-400 font-mono">{start}</span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">End</span>
          <span className="text-slate-300 font-mono font-medium">{end}</span>
        </div>
        <div className="flex justify-between items-center pt-1 border-t border-slate-700/40">
          <span className="text-xs text-slate-500">Return</span>
          <div className="flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-sm font-bold text-emerald-400">{ret}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompareFooter() {
  return (
    <div className="flex flex-wrap gap-4">
      <CompareCard color={CMP_A} icon={<XrpIcon />} name="XRP" symbol="XRP" start="A$1.19" end="A$1.40" ret="+18.0%" winner />
      <CompareCard color={CMP_B} icon={<BtcIcon />} name="Bitcoin" symbol="BTC" start="A$153,271" end="A$164,000" ret="+7.0%" />
    </div>
  );
}

export function StrategyPreview() {
  const { open } = useUpgradePrompt();
  const [tab, setTab] = useState<Tab>('Split Buy');

  const body   = tab === 'Compare' ? <CompareBody /> : tab === 'Split Buy' ? <BuyBody /> : <SellBody />;
  const footer = tab === 'Compare' ? <CompareFooter /> : tab === 'Split Buy' ? <BuyFooter /> : <SellFooter />;

  return (
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/40 border border-slate-700">
      {/* Clear "this is an example, not your portfolio" badge — visible on all tabs */}
      <div className="absolute top-3.5 right-4 z-20">
        <span
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
          style={{ background: 'rgba(212,180,131,0.14)', color: '#d4b483', border: '0.5px solid rgba(212,180,131,0.32)' }}
        >
          Example plan
        </span>
      </div>

      {/* Sub-tab bar — the ONLY interactive part; swaps which example renders below */}
      <div className="px-5 pt-5">
        <div className="flex gap-1 overflow-x-auto" style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}>
          {SUB_TABS.map((label) => {
            const isActive = label === tab;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setTab(label)}
                className="whitespace-nowrap transition-colors"
                style={{
                  background: 'transparent',
                  padding: '10px 14px',
                  fontSize: '13px',
                  borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                  marginBottom: '-0.5px',
                  color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                  fontWeight: isActive ? 500 : 400,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Example body — display only */}
      <div className="px-5 pt-4 space-y-4 pointer-events-none select-none" aria-hidden="true">
        {body}
      </div>

      {/* Lower region: the example's summary/cards fade into the upgrade CTA */}
      <div className="relative">
        <div className="px-5 pt-4 pb-6 pointer-events-none select-none" aria-hidden="true">
          {footer}
        </div>
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgb(var(--sl-900) / 0) 0%, rgb(var(--sl-900) / 0.72) 42%, rgb(var(--sl-900)) 78%)' }}
        />
        <div className="relative px-6 pb-8 pt-2 flex flex-col items-center text-center">
          <p className="text-[13px] max-w-md mb-3" style={{ color: 'var(--color-sub)' }}>
            {FEATURE_GATES.strategy.message}
          </p>
          <button
            type="button"
            onClick={() => open(FEATURE_GATES.strategy)}
            className="flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Upgrade
          </button>
        </div>
      </div>
    </div>
  );
}
